/**
 * What kind of device this is — read once, before the first game, so each game
 * can START at the right level of detail instead of discovering it mid-round.
 *
 * 🔴 WHY A PROFILE AS WELL AS THE FPS LADDER. `vision/tier/tierMachine` adapts
 * to measured frame rate, and that stays the authority while a child plays. But
 * evidence takes seconds: every session used to open on LOW scenery and swap to
 * high mid-round on a strong laptop (a visible change a child notices), and a
 * weak Chromebook got the full 4096 px Kalqy World textures before anything had
 * measured it. The profile is the prior; the ladder corrects it.
 *
 * ⚠️ THREE SIGNALS, ALL CHEAP AND ALL LOCAL. CPU cores and memory from
 * `navigator`, and the GPU's renderer string from one throwaway WebGL context
 * (released immediately). Nothing is sent anywhere; the result is kept in
 * memory and, as a hint for the next session, in `localStorage`.
 *
 * ⚠️ THE GPU STRING IS A HINT, NOT A PROMISE. Browsers mask or round it (Safari
 * says "Apple GPU"; Firefox may generalise), so an unrecognised GPU is `mid`,
 * never `low` — a wrong `low` costs a strong laptop its detail for nothing, and
 * the FPS ladder still catches a weak one within seconds.
 */

export type DeviceClass = 'low' | 'mid' | 'high'
export type GpuClass = 'software' | 'low' | 'mid' | 'high' | 'unknown'

export interface DeviceSignals {
  cores: number | null
  memoryGB: number | null
  gpu: string | null
  maxTextureSize: number | null
}

export interface DeviceProfile extends DeviceSignals {
  deviceClass: DeviceClass
  gpuClass: GpuClass
  /** Why — for the teacher diagnostics chip and for support, never shown to a child. */
  reasons: string[]
}

/** Renderer string → GPU class. Order matters: the first match wins. */
const GPU_RULES: readonly (readonly [RegExp, GpuClass])[] = [
  [/swiftshader|llvmpipe|softpipe|microsoft basic render|software/i, 'software'],
  [/nvidia|geforce|rtx|gtx|quadro|radeon (?:rx|pro)|apple m\d|apple gpu/i, 'high'],
  [/iris(?:\(r\))? xe|intel.*\barc\b|radeon(?! r[2-5])|adreno.*\b(?:6[4-9]\d|7\d\d)\b|mali-g7\d/i, 'mid'],
  [/intel.*(?:hd|uhd) graphics|mali-[gt]\d|adreno.*\b[2-6]\d\d\b|powervr|videocore|vivante/i, 'low'],
]

export function classifyGpu(renderer: string | null): GpuClass {
  if (!renderer) return 'unknown'
  for (const [re, cls] of GPU_RULES) if (re.test(renderer)) return cls
  return 'unknown'
}

/** The pure half: signals → class. Exported so the rules can be tested without a browser. */
export function classifyDevice(s: DeviceSignals): DeviceProfile {
  const gpuClass = classifyGpu(s.gpu)
  const reasons: string[] = []
  let cls: DeviceClass = 'mid'

  if (gpuClass === 'software') {
    reasons.push('no GPU acceleration')
    cls = 'low'
  } else if ((s.cores !== null && s.cores <= 2) || (s.memoryGB !== null && s.memoryGB <= 2)) {
    reasons.push(`${String(s.cores ?? '?')} cores, ${String(s.memoryGB ?? '?')} GB`)
    cls = 'low'
  } else if (s.maxTextureSize !== null && s.maxTextureSize < 4096) {
    reasons.push(`max texture ${String(s.maxTextureSize)}`)
    cls = 'low'
  } else if (gpuClass === 'low') {
    /* An integrated GPU on a big machine is still an integrated GPU, but it is not a Chromebook. */
    const roomy = (s.cores ?? 0) >= 8 && (s.memoryGB ?? 0) >= 8
    reasons.push(roomy ? 'integrated GPU, roomy CPU' : 'integrated GPU')
    cls = roomy ? 'mid' : 'low'
  } else if (gpuClass === 'high') {
    reasons.push('discrete or Apple GPU')
    cls = 'high'
  } else {
    reasons.push(gpuClass === 'mid' ? 'capable integrated GPU' : 'unrecognised GPU')
  }
  return { ...s, gpuClass, deviceClass: cls, reasons }
}

/* ── the browser half ──────────────────────────────────────────────────────── */

const OVERRIDE_KEY = 'kalqy.deviceClass'
const isClass = (v: unknown): v is DeviceClass => v === 'low' || v === 'mid' || v === 'high'

function readSignals(): DeviceSignals {
  const nav = globalThis.navigator as (Navigator & { deviceMemory?: number }) | undefined
  const signals: DeviceSignals = {
    cores: nav?.hardwareConcurrency ?? null,
    memoryGB: nav?.deviceMemory ?? null,
    gpu: null,
    maxTextureSize: null,
  }
  if (typeof document === 'undefined') return signals
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null
    if (!gl) {
      signals.gpu = 'software (no WebGL)'
      return signals
    }
    const info = gl.getExtension('WEBGL_debug_renderer_info')
    signals.gpu = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER))
    signals.maxTextureSize = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE))
    gl.getExtension('WEBGL_lose_context')?.loseContext()
  } catch {
    /* A browser that refuses the probe is simply `mid`. */
  }
  return signals
}

let cached: DeviceProfile | null = null

/**
 * This device's profile, probed on first call. A QA override — `?device=low`
 * once, remembered in `localStorage` until `?device=auto` — wins, so a tester
 * can see the low path on a fast laptop.
 */
export function deviceProfile(): DeviceProfile {
  if (cached) return cached
  const profile = classifyDevice(readSignals())
  const override = readOverride()
  cached = override ? { ...profile, deviceClass: override, reasons: [...profile.reasons, `override: ${override}`] } : profile
  return cached
}

/** Shorthand for the one value most callers want. */
export function deviceClass(): DeviceClass {
  return deviceProfile().deviceClass
}

/**
 * Reads a `?device=` QA override into storage. Called once at startup, so the
 * override holds on whatever screen the tester opened, not only the one that
 * first asks for the profile.
 */
export function captureDeviceOverride(): void {
  readOverride()
}

function readOverride(): DeviceClass | null {
  try {
    const param = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('device')
    if (param === 'auto') localStorage.removeItem(OVERRIDE_KEY)
    else if (isClass(param)) localStorage.setItem(OVERRIDE_KEY, param)
    const stored = localStorage.getItem(OVERRIDE_KEY)
    return isClass(stored) ? stored : null
  } catch {
    return null
  }
}

/* ── what the last session learned ─────────────────────────────────────────── */

const LEARNED_KEY = 'kalqy.learnedArt'

/**
 * The scenery level this device ended its last session holding, if any — the
 * FPS ladder's verdict, carried forward so the next session starts there.
 */
export function learnedArtQuality(): 'low' | 'high' | null {
  try {
    const v = localStorage.getItem(LEARNED_KEY)
    return v === 'low' || v === 'high' ? v : null
  } catch {
    return null
  }
}

export function rememberArtQuality(quality: 'low' | 'high'): void {
  try {
    localStorage.setItem(LEARNED_KEY, quality)
  } catch {
    /* Private mode: the next session simply starts from the profile again. */
  }
}

/** Test seam. */
export function __resetDeviceProfileForTests(): void {
  cached = null
}
